import {ChangeEventHandler, OptionHTMLAttributes} from 'react';
import {useIntl} from 'react-intl';
import {Col, Form, Row} from 'react-bootstrap';
import {FormikProps} from 'formik';
import {FileToUpload} from './api';
import SwitchInputControlled from '../../../modules/form/SwitchInputControlled';
import TextInputControlled from '../../../modules/form/TextInputControlled';
import FileInputControlled from '../../../modules/form/FileInputControlled';
import DateInputControlled from '../../../modules/form/DateInputControlled';
import Select from '../../../common/components/select/Select';
import {capitalizeFirstLetter} from '../../../common/tools/text';

interface AdminDocumentFormProps {
    config: FormikProps<FileToUpload>;
    isLoading?: boolean;
    selectTypeOptions: OptionHTMLAttributes<string>[];
    selectLangueOptions: OptionHTMLAttributes<string>[];
    resetCategoryKey: number;
    fileOnChangeHandler: ChangeEventHandler;
}

const AdminDocumentForm = ({
    config,
    selectTypeOptions,
    selectLangueOptions,
    resetCategoryKey,
    fileOnChangeHandler,
}: AdminDocumentFormProps): JSX.Element => {
    const intl = useIntl();
    const {
        values,
        touched,
        errors,
        handleChange,
        handleBlur,
        handleSubmit,
        handleReset,
        setFieldValue,
    } = config;

    return (
        <Form onSubmit={handleSubmit} encType="multipart/form-data">
            <Row className="g-5">
                <Col sm={12}>
                    <FileInputControlled
                        required={true}
                        value={values.file}
                        onChange={fileOnChangeHandler}
                        onReset={handleReset}
                        touched={touched.file}
                        errors={errors.file}
                        name="file"
                        label={intl.formatMessage({id: 'ADMIN.DOCUMENTS.SELECT.FIME'})}
                    />
                </Col>
                <Col sm={12}>
                    <TextInputControlled
                        label={intl.formatMessage({id: 'ADMIN.DOCUMENTS.SELECT.RENAME'})}
                        value={values.fileName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onReset={handleReset}
                        touched={touched.fileName}
                        errors={errors.fileName}
                        name="fileName"
                    />
                </Col>
                <Col sm={12}>
                    <Form.Group>
                        <Select<OptionHTMLAttributes<string>>
                            label={intl.formatMessage({id: 'ADMIN.DOCUMENTS.SELECT.CATEGORY'})}
                            isClearable={true}
                            isSearchable={false}
                            name="category"
                            onChange={(category) => {
                                setFieldValue('category', category);
                            }}
                            value={values.category}
                            options={selectTypeOptions}
                            getOptionLabel={(option) => capitalizeFirstLetter(option.label ?? '')}
                            placeholder={intl.formatMessage({id: 'ADMIN.DOC.REASON_PLACEHOLDER'})}
                            key={resetCategoryKey}
                            touched={touched.category}
                            error={errors.category}
                        />
                    </Form.Group>
                </Col>
                <Col sm={12}>
                    <Form.Group>
                        <Select<OptionHTMLAttributes<string>>
                            label={intl.formatMessage({id: 'ADMIN.DOCUMENTS.SELECT.LANGUE'})}
                            isClearable={true}
                            isSearchable={false}
                            name="langue"
                            onChange={(langue) => {
                                setFieldValue('langue', langue);
                            }}
                            value={values.langue}
                            options={selectLangueOptions}
                            placeholder={intl.formatMessage({
                                id: 'ADMIN.DOCUMENTS.SELECT.LANGUE.PLACEHOLDER',
                            })}
                            key={resetCategoryKey}
                            touched={touched.langue}
                            error={errors.langue}
                        />
                    </Form.Group>
                </Col>
                <Col sm={12}>
                    <SwitchInputControlled
                        label={intl.formatMessage({id: 'ADMIN.DOCUMENTS.SELECT.VISIBLE'})}
                        value={values.visible.toString()}
                        defaultValue={values.visible}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onReset={handleReset}
                        touched={touched.visible}
                        errors={errors.visible}
                        name="visible"
                    />
                </Col>
                <Col style={{visibility: values.visible ? 'hidden' : 'visible'}}>
                    <DateInputControlled
                        name="start_date"
                        label={intl.formatMessage({id: 'ADMIN.NEWS.FORM.DATE.STARTED'})}
                        value={values.start_date}
                        disabled={values.visible}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onReset={handleReset}
                        touched={touched.start_date}
                        errors={errors.start_date}
                        min={new Date().toISOString().slice(0, -8)}
                    ></DateInputControlled>
                </Col>
            </Row>
        </Form>
    );
};

export default AdminDocumentForm;
